// Copyright 2023 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Col, Result, Row, Spin, Steps} from "antd";
import {withRouter} from "react-router-dom";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as Setting from "../Setting";
import i18next from "i18next";
import * as MfaBackend from "../backend/MfaBackend";
import {CheckOutlined, KeyOutlined, UserOutlined} from "@ant-design/icons";
import CheckPasswordForm from "./mfa/CheckPasswordForm";
import MfaEnableForm from "./mfa/MfaEnableForm";
import {MfaVerifyForm} from "./mfa/MfaVerifyForm";

export const EmailMfaType = "email";
export const SmsMfaType = "sms";
export const TotpMfaType = "app";
export const RadiusMfaType = "radius";
export const PushMfaType = "push";
export const RecoveryMfaType = "recovery";

// Paths that a third-party application sends the user to in order to sign in. Only
// these carry a "return to the application" intent worth resuming after MFA setup;
// Casdoor's own login pages ("/login", "/login/:owner") have nowhere to go back to.
const APP_SIGNIN_PATHS = [
  "/login/oauth/authorize",
  "/login/saml/authorize",
  "/cas/",
];

function parseUrl(url) {
  if (!url) {
    return null;
  }
  try {
    return new URL(url, window.location.origin);
  } catch (e) {
    return null;
  }
}

// A bare Casdoor root is not a usable target: it is what the "/callback" flow used to
// record (window.location.origin), and following it just drops the user in Casdoor.
function isResumableUrl(url) {
  const parsed = parseUrl(url);
  return parsed !== null && !(parsed.pathname === "/" && parsed.search === "");
}

function isAppSigninUrl(url) {
  const parsed = parseUrl(url);
  return parsed !== null && APP_SIGNIN_PATHS.some((path) => parsed.pathname.startsWith(path));
}

// Where to send the user once MFA is enabled.
//
// "mfaRedirectUrl" is written by the login handshake that answered "RequiredMfa", but
// MFA setup is also reachable without it: the client-side enforcement in App.js, the
// "enable MFA" notification, and any page load made while a required MFA item is still
// missing all push straight to /mfa/setup. In those cases fall back to the sign-in URL
// that LoginPage records on every render, so the user still lands back in the
// application that sent them here instead of on Casdoor's account page.
export function getMfaSetupRedirectUrl() {
  const mfaRedirectUrl = localStorage.getItem("mfaRedirectUrl");
  if (isResumableUrl(mfaRedirectUrl)) {
    return mfaRedirectUrl;
  }

  const signinUrl = localStorage.getItem("signinUrl");
  return isAppSigninUrl(signinUrl) ? signinUrl : null;
}

const ResumeSigninKey = "mfaSetupResumeSigninUrl";

// Back on the sign-in page after MFA setup, the user is already signed in, and the page would ask
// them to sign in again ("Continue with ..."). The sign-in page finishes the sign-in instead when
// it is the one MFA setup sent the user to: one tab, one page load.
function markSigninResume(url) {
  sessionStorage.setItem(ResumeSigninKey, new URL(url, window.location.origin).href);
}

export function takeSigninResume() {
  const url = sessionStorage.getItem(ResumeSigninKey);
  sessionStorage.removeItem(ResumeSigninKey);
  return url !== null && url === window.location.href;
}

class MfaSetupPage extends React.Component {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(props.location.search);
    const {location} = this.props;
    this.state = {
      account: props.account,
      application: null,
      applicationError: null,
      applicationName: props.account.signupApplication ?? localStorage.getItem("applicationName") ?? "",
      current: location.state?.from !== undefined ? 1 : 0,
      mfaProps: null,
      mfaType: params.get("mfaType") ?? SmsMfaType,
      isPromptPage: props.isPromptPage || location.state?.from !== undefined,
      loading: false,
    };
  }

  componentDidMount() {
    this.getApplication();
    if (this.state.current === 1) {
      this.setState({
        loading: true,
      });

      setTimeout(() => {
        this.initMfaProps();
      }, 200);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.state.mfaType !== prevState.mfaType || this.state.current !== prevState.current) {
      if (this.state.current === 1) {
        this.initMfaProps();
      }
    }
  }

  getApplication() {
    // LDAP-synced (and other externally-created) users may have an empty "signupApplication",
    // so fall back to the user's organization application via the user-aware endpoint.
    const promise = this.state.applicationName !== ""
      ? ApplicationBackend.getApplication("admin", this.state.applicationName)
      : ApplicationBackend.getUserApplication(this.state.account.owner, this.state.account.name);

    promise.then((res) => {
      if (res !== null) {
        if (res.status === "error") {
          this.setState({applicationError: res.msg});
          Setting.showMessage("error", res.msg);
          return;
        }
        if (!res.data) {
          const msg = i18next.t("general:Failed to get");
          this.setState({applicationError: msg});
          Setting.showMessage("error", msg);
          return;
        }
        this.setState({
          application: res.data,
        });
      } else {
        const msg = i18next.t("general:Failed to get");
        this.setState({applicationError: msg});
        Setting.showMessage("error", msg);
      }
    });
  }

  initMfaProps() {
    MfaBackend.MfaSetupInitiate({
      mfaType: this.state.mfaType,
      ...this.getUser(),
    }).then((res) => {
      if (res.status === "ok") {
        this.setState({
          mfaProps: res.data,
          loading: false,
        });
      } else {
        this.setState({
          loading: false,
        });
        Setting.showMessage("error", i18next.t("mfa:Failed to initiate MFA"));
      }
    });
  }

  getUser() {
    return this.props.account;
  }

  renderMfaTypeSwitch() {
    const renderSmsLink = () => {
      if (this.state.mfaType === SmsMfaType) {
        return null;
      }
      return (<Button type={"link"} onClick={() => {
        this.setState({
          mfaType: SmsMfaType,
        });
        this.props.history.push(`/mfa/setup?mfaType=${SmsMfaType}`);
      }
      }>{i18next.t("mfa:Use SMS")}</Button>
      );
    };

    const renderEmailLink = () => {
      if (this.state.mfaType === EmailMfaType) {
        return null;
      }
      return (<Button type={"link"} onClick={() => {
        this.setState({
          mfaType: EmailMfaType,
        });
        this.props.history.push(`/mfa/setup?mfaType=${EmailMfaType}`);
      }
      }>{i18next.t("mfa:Use Email")}</Button>
      );
    };

    const renderTotpLink = () => {
      if (this.state.mfaType === TotpMfaType) {
        return null;
      }
      return (<Button type={"link"} onClick={() => {
        this.setState({
          mfaType: TotpMfaType,
        });
        this.props.history.push(`/mfa/setup?mfaType=${TotpMfaType}`);
      }
      }>{i18next.t("mfa:Use Authenticator App")}</Button>
      );
    };

    const renderRadiusLink = () => {
      if (this.state.mfaType === RadiusMfaType) {
        return null;
      }
      return (<Button type={"link"} onClick={() => {
        this.setState({
          mfaType: RadiusMfaType,
        });
        this.props.history.push(`/mfa/setup?mfaType=${RadiusMfaType}`);
      }
      }>{i18next.t("mfa:Use Radius")}</Button>
      );
    };

    const renderPushLink = () => {
      if (this.state.mfaType === PushMfaType) {
        return null;
      }
      return (<Button type={"link"} onClick={() => {
        this.setState({
          mfaType: PushMfaType,
        });
        this.props.history.push(`/mfa/setup?mfaType=${PushMfaType}`);
      }
      }>{i18next.t("mfa:Use Push Notification")}</Button>
      );
    };

    return !this.state.isPromptPage ? (
      <React.Fragment>
        {renderSmsLink()}
        {renderEmailLink()}
        {renderTotpLink()}
        {renderRadiusLink()}
        {renderPushLink()}
      </React.Fragment>
    ) : null;
  }

  onMfaEnabled() {
    this.props.onfinish();

    const redirectUrl = getMfaSetupRedirectUrl();
    // Always drop it: a value left behind by an abandoned setup would otherwise
    // hijack the next one and bounce the user into a stale application.
    localStorage.removeItem("mfaRedirectUrl");
    if (redirectUrl !== null) {
      if (isAppSigninUrl(redirectUrl)) {
        markSigninResume(redirectUrl);
      }
      Setting.goToLink(redirectUrl);
    } else {
      this.props.history.push("/account");
    }
  }

  renderStep() {
    switch (this.state.current) {
    case 0:
      return (
        <CheckPasswordForm
          user={this.getUser()}
          onSuccess={() => {
            this.setState({
              current: this.state.current + 1,
            });
          }}
          onFail={(res) => {
            Setting.showMessage("error", i18next.t("mfa:Failed to initiate MFA") + ": " + res.msg);
          }}
        />
      );
    case 1:
      if (this.state.applicationError) {
        return (
          <Result
            status="error"
            title={i18next.t("mfa:Failed to initiate MFA")}
            subTitle={this.state.applicationError}
          />
        );
      }
      return (
        <div>
          <MfaVerifyForm
            mfaProps={this.state.mfaProps}
            application={this.state.application}
            user={this.props.account}
            onSuccess={(res) => {
              this.setState({
                dest: res.dest,
                countryCode: res.countryCode,
                current: this.state.current + 1,
              });
            }}
            onFail={(res) => {
              Setting.showMessage("error", i18next.t("general:Failed to verify") + ": " + res.msg);
            }}
          />
          <Col span={24} style={{display: "flex", justifyContent: "left"}}>
            {this.renderMfaTypeSwitch()}
          </Col>
        </div>
      );
    case 2:
      return (
        <MfaEnableForm user={this.getUser()} mfaType={this.state.mfaType} secret={this.state.mfaProps.secret} recoveryCodes={this.state.mfaProps.recoveryCodes} dest={this.state.dest} countryCode={this.state.countryCode}
          onSuccess={() => {
            Setting.showMessage("success", i18next.t("general:Enabled successfully"));
            this.onMfaEnabled();
          }}
          onFail={(res) => {
            Setting.showMessage("error", `${i18next.t("general:Failed to enable")}: ${res.msg}`);
          }} />
      );
    default:
      return null;
    }
  }

  render() {
    if (!this.props.account) {
      return (
        <Result
          status="403"
          title="403 Unauthorized"
          subTitle={i18next.t("general:Sorry, you do not have permission to access this page or logged in status invalid.")}
          extra={<a href="/web/public"><Button type="primary">{i18next.t("general:Back Home")}</Button></a>}
        />
      );
    }

    return (
      <Row>
        <Col span={24} style={{justifyContent: "center"}}>
          <Row>
            <Col span={24}>
              <p style={{textAlign: "center", fontSize: "28px"}}>
                {i18next.t("mfa:Protect your account with Multi-factor authentication")}</p>
              <p style={{textAlign: "center", fontSize: "16px", marginTop: "10px"}}>{i18next.t("mfa:Each time you sign in to your Account, you'll need your password and a authentication code")}</p>
            </Col>
          </Row>
          <Spin spinning={this.state.loading}>
            <Steps current={this.state.current}
              items={[
                {title: i18next.t("mfa:Verify Password"), icon: <UserOutlined />},
                {title: i18next.t("mfa:Verify Code"), icon: <KeyOutlined />},
                {title: i18next.t("general:Enable"), icon: <CheckOutlined />},
              ]}
              style={{width: "90%", maxWidth: "500px", margin: "auto", marginTop: "50px",
              }} >
            </Steps>
          </Spin>
        </Col>
        <Col span={24} style={{display: "flex", justifyContent: "center"}}>
          <div style={{marginTop: "10px", textAlign: "center"}}>
            {this.renderStep()}
          </div>
        </Col>
      </Row>
    );
  }
}

export default withRouter(MfaSetupPage);
